import React, { useState } from "react";
import {
  Box,
  Typography,
  ListItemAvatar,
  Avatar,
  TextField,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"; // Import More icon
import CloseIcon from "@mui/icons-material/Close"; // Import Close icon
import SendIcon from "@mui/icons-material/Send"; // Import Send icon
import { MessageData } from "./Messages"; // Import the MessageData interface

interface MessageProps {
  messageData: MessageData; // The message data
  onEditMessage: (
    IdToEdit: number | null,
    DateToEdit: string | null,
    newMessageContent: string
  ) => void; // Function to edit a message
  onDeleteMessage: (
    IdToDelete: number | null,
    DateToDelete: string | null
  ) => void; // Updated to accept two parameters
  currentUserId: number; // Add currentUserId prop
}

const Message: React.FC<MessageProps> = ({
  messageData,
  onEditMessage,
  onDeleteMessage,
  currentUserId, // Destructure currentUserId
}) => {
  const [isEditing, setIsEditing] = useState(false); // State to track if the message is being edited
  const [editMessageContent, setEditMessageContent] = useState(
    messageData.message
  ); // State for editing message content
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null); // State for the menu anchor

  const handleEditToggle = () => {
    setIsEditing((prev) => !prev); // Toggle editing state
  };

  const handleUpdate = () => {
    const IdToEdit = messageData.id !== undefined ? messageData.id : null; // Pass ID if available
    const DateToEdit = messageData.created_at ? messageData.created_at : null; // Pass created_at if available

    onEditMessage(IdToEdit, DateToEdit, editMessageContent); // Call onEditMessage with the current values
    setIsEditing(false); // Exit editing mode
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget); // Set the anchor for the menu
  };

  const handleCloseMenu = () => {
    setAnchorEl(null); // Close the menu
  };

  const handleEditMessage = () => {
    handleEditToggle(); // Toggle edit mode
    handleCloseMenu(); // Close the menu
  };

  const handleDeleteMessage = () => {
    const IdToDelete = messageData.id !== undefined ? messageData.id : null; // Pass ID if available
    const DateToDelete = messageData.created_at ? messageData.created_at : null; // Pass created_at if available

    onDeleteMessage(IdToDelete, DateToDelete); // Now passing both values
    handleCloseMenu(); // Close the menu
  };

  // Function to format the time since the message was sent
  const formatTime = (timestamp: string) => {
    const messageDate = new Date(timestamp); // This creates a date object in local time
    const now = new Date(); // Current time in local time
    const diffInSeconds = Math.floor(
      (now.getTime() - messageDate.getTime()) / 1000
    );

    if (diffInSeconds < 60) {
      return "Sent less than a minute ago";
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `Sent ${minutes} minute${minutes !== 1 ? "s" : ""} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `Sent ${hours} hour${hours !== 1 ? "s" : ""} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `Sent ${days} day${days !== 1 ? "s" : ""} ago`;
    }
  };

  return (
    <Box display="flex" alignItems="flex-start" mb={1}>
      <ListItemAvatar sx={{ mt: 2.1 }}>
        <Avatar
          src={messageData.sender_avatar_url ?? ""}
          alt={messageData.sender_name ?? ""}
        />
      </ListItemAvatar>
      <Box display="flex" flexDirection="column" ml={1} width="100%">
        <Typography variant="body2" fontWeight="bold">
          {messageData.sender_name}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: -0.5, mb: 0.5 }}
        >
          {messageData.selected_role_details}
        </Typography>
        {isEditing ? (
          <Box display="flex" flexDirection="column" width="100%">
            <TextField
              value={editMessageContent}
              onChange={(e) => setEditMessageContent(e.target.value)}
              fullWidth
              variant="outlined"
              multiline // Enable multiline
              rows={4} // Set the number of visible rows
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleUpdate(); // Update on Enter key press
                } else if (e.key === "Escape") {
                  setIsEditing(false); // Exit edit mode on Escape
                  setEditMessageContent(messageData.message); // Reset to original message
                }
              }}
              sx={{ mt: 1 }} // Add margin-top for spacing
            />
            <Box display="flex" justifyContent="flex-end" mt={1}>
              <IconButton
                onClick={() => {
                  setIsEditing(false); // Exit edit mode
                  setEditMessageContent(messageData.message); // Reset to original message
                }}
                color="error"
              >
                <CloseIcon />
              </IconButton>
              <IconButton onClick={handleUpdate} color="primary">
                <SendIcon />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <Box
            bgcolor="primary.main"
            color="white"
            borderRadius={3}
            p={1}
            maxWidth="100%"
            sx={{ wordWrap: "break-word", overflowWrap: "break-word" }} // Allow text to wrap
          >
            <Typography variant="body1">{messageData.message}</Typography>
          </Box>
        )}
        <Box display="flex" alignItems="center" sx={{ mt: -0.8, mb: 0.25 }}>
          {!isEditing && ( // Only show time and icon when not editing
            <>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ mt: messageData.sender_id === currentUserId ? 0 : 1 }}
              >
                {formatTime(messageData.created_at)}{" "}
              </Typography>
              <Box
                sx={{
                  width: "24px",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                {messageData.sender_id === currentUserId ? (
                  <IconButton
                    onClick={handleMenuClick} // Open menu on click
                    sx={{ ml: 1 }}
                  >
                    <MoreHorizIcon />
                  </IconButton>
                ) : (
                  <div style={{ visibility: "hidden" }}>
                    <MoreHorizIcon />
                  </div>
                )}
              </Box>
            </>
          )}
        </Box>
        {/* Menu for editing and deleting messages */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
        >
          <MenuItem onClick={handleEditMessage}>Edit</MenuItem>
          <MenuItem onClick={handleDeleteMessage}>Delete</MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default Message;
